import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import communityService, { CommunityMember } from '@/services/communityService';
import { CommunityMembersSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/Button';

// Helper function to format date
const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  } catch {
    return dateStr || '-';
  }
};

// Helper function to get name initials
const getInitials = (name?: string): string => {
  if (!name) return 'KM';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const ClientCommunityMembersPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);

  const limit = 10;

  // Scroll to top on mount and page change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTop = 0;
    }
  }, [page]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1); // Reset to page 1 on new search
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch members API
  useEffect(() => {
    const fetchMembers = async () => {
      if (!slug) return;
      try {
        setLoading(true);
        const res = await communityService.getCommunityMembers(slug, {
          page,
          limit,
          search: search || undefined,
        });

        if (res.success && res.data) {
          setMembers(res.data.members || []);
          setTotalCount(res.data.total_count || 0);
        } else {
          setMembers([]);
          setTotalCount(0);
        }
      } catch (err) {
        console.error('Failed to fetch community members:', err);
        setMembers([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchMembers();
  }, [slug, page, search]);

  const totalPages = Math.ceil(totalCount / limit) || 1;
  const startItem = totalCount > 0 ? (page - 1) * limit + 1 : 0;
  const endItem = Math.min(page * limit, totalCount);

  // Background colors for initials
  const bgColors = [
    'bg-orange-500',
    'bg-blue-500',
    'bg-amber-700',
    'bg-emerald-500',
    'bg-purple-500',
    'bg-teal-500',
    'bg-indigo-500',
    'bg-rose-500',
  ];

  // Helper for pagination page numbers array
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-12 font-sans">
      <div className="max-w-md mx-auto px-4 pt-4 space-y-4">
        {/* Search Bar */}
        <div className="relative">
          <Icon
            icon="ph:magnifying-glass-bold"
            className="w-4 h-4 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"
          />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Cari nama anggota"
            className="w-full bg-white h-11 pl-11 pr-4 rounded-full border border-gray-200 text-xs text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] transition-colors shadow-2xs"
          />
        </div>

        {loading ? (
          <CommunityMembersSkeleton />
        ) : (
          <>
            {/* Count Info Bar */}
            <div className="flex items-center justify-between text-xs px-1">
              <span className="text-gray-400 font-medium">
                Menampilkan {startItem}–{endItem} dari {totalCount}
              </span>
              <span className="text-[#FC480D] font-extrabold">{totalCount} anggota</span>
            </div>

            {/* Members Card List */}
            <Card className="overflow-hidden divide-y divide-gray-100">
              {members.length === 0 ? (
                <div className="py-12 px-4 text-center space-y-2">
                  <Icon icon="ph:users-three" className="w-10 h-10 text-gray-300 mx-auto" />
                  <p className="text-xs text-gray-400 font-medium">
                    {search ? 'Anggota tidak ditemukan' : 'Belum ada anggota komunitas'}
                  </p>
                </div>
              ) : (
            members.map((m: any, idx: number) => {
              const itemIndex = startItem + idx;
              const name =
                m.community_user?.name ||
                m.community_user?.nickname ||
                m.user?.name ||
                m.name ||
                'Anggota';
              const avatar =
                m.community_user?.avatar ||
                m.user?.avatar_url ||
                m.avatar_url ||
                m.avatar;
              const joinDate = formatDate(m.join_date || m.created_at);
              const position = m.position || m.role || 'Member';
              const initials = getInitials(name);
              const bg = bgColors[idx % bgColors.length];

              const isHostOrAdmin = position === 'host' || position === 'admin';
              const username =
                m.community_user?.username ||
                m.user?.username ||
                m.username ||
                m.user_slug ||
                m.slug;

              return (
                <div
                  key={m.id || idx}
                  onClick={() => {
                    if (username) {
                      navigate(`/profile/${username}`);
                    }
                  }}
                  className="p-3.5 flex items-center justify-between gap-3 hover:bg-gray-50/70 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    {/* Rank / Number */}
                    <span className="text-xs font-semibold text-gray-400 w-5 text-center shrink-0">
                      {itemIndex}
                    </span>

                    {/* Avatar */}
                    {avatar ? (
                      <img
                        src={avatar}
                        alt={name}
                        className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100"
                      />
                    ) : (
                      <div
                        className={`w-10 h-10 rounded-full ${bg} text-white font-bold text-xs flex items-center justify-center shrink-0 shadow-2xs`}
                      >
                        {initials}
                      </div>
                    )}

                    {/* Name & Join Date */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <h4 className="text-xs font-bold text-[#0E1726] truncate">{name}</h4>
                        {isHostOrAdmin && (
                          <Icon icon="ph:seal-check-fill" className="w-3.5 h-3.5 text-blue-500 shrink-0" />
                        )}
                      </div>
                      <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1 mt-0.5">
                        <Icon icon="ph:calendar-blank-bold" className="w-3 h-3 text-gray-400" />
                        <span>Gabung {joinDate}</span>
                      </p>
                    </div>
                  </div>

                  {/* Position Badge / Tier */}
                  <Badge
                    className={`text-[11px] font-bold px-3 py-1 rounded-full shrink-0 border capitalize ${
                      position === 'host'
                        ? 'bg-amber-50 text-amber-700 border-amber-200'
                        : position === 'admin'
                        ? 'bg-blue-50 text-blue-700 border-blue-200'
                        : 'bg-amber-50/60 text-amber-800 border-amber-200/60'
                    }`}
                  >
                    {position}
                  </Badge>
                </div>
              );
            })
          )}
        </Card>

        {/* Pagination Bar */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2 pb-6">
            {/* Prev Page Button */}
            <Button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white p-0 flex items-center justify-center text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-2xs cursor-pointer"
            >
              <Icon icon="ph:caret-left-bold" className="w-4 h-4" />
            </Button>

            {/* Page Numbers */}
            {getPageNumbers().map((p, idx) =>
              p === '...' ? (
                <span key={`dots-${idx}`} className="text-gray-400 text-xs px-1">
                  ...
                </span>
              ) : (
                <Button
                  key={`page-${p}`}
                  type="button"
                  onClick={() => setPage(p as number)}
                  className={`w-8 h-8 rounded-full text-xs font-bold transition-colors cursor-pointer p-0 flex items-center justify-center ${
                    page === p
                      ? 'bg-[#FC480D] text-white shadow-2xs hover:bg-[#D83B08]'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </Button>
              )
            )}

            {/* Next Page Button */}
            <Button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="w-8 h-8 rounded-full border border-gray-200 bg-white p-0 flex items-center justify-center text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors shadow-2xs cursor-pointer"
            >
              <Icon icon="ph:caret-right-bold" className="w-4 h-4" />
            </Button>
          </div>
        )}
      </>
    )}
      </div>
    </div>
  );
};

export default ClientCommunityMembersPage;
